import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { isIOS, isNativeApp, webOrigin } from "@/lib/nativeBridge";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import CompBanner from "@/components/CompBanner";
import { Button } from "@/components/ui/button";
import { ChartBarIcon as ChartBar, CrownIcon as Crown, TrendUpIcon as TrendUp, TrendDownIcon as TrendDown, LightningIcon as Lightning, MagnifyingGlassIcon as MagnifyingGlass, SparkleIcon as Sparkle, ArrowRightIcon as ArrowRight, EyeIcon as Eye, ChatCircleIcon as ChatCircle, StorefrontIcon as Storefront, CurrencyDollarIcon as CurrencyDollar, ClockIcon as Clock, TagIcon as Tag, HeartIcon as Heart, CalendarBlankIcon as CalendarBlank, CameraPlusIcon as CameraPlus, VideoCameraIcon as VideoCamera, UserCircleIcon as UserCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils-app";
import IapUpgradeButton from "@/components/IapUpgradeButton";

const INSIGHT_ICONS = {
  lightning: Lightning,
  magnifyingglass: MagnifyingGlass,
  trendup: TrendUp,
  sparkle: Sparkle,
};

export default function BreederAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [locked, setLocked] = useState(false);
  const [showDemo, setShowDemo] = useState(false);
  // When set, the listings table filters down to that bucket's listings
  // (e.g. "0-1" photos) so a breeder tapping the photo-diagnostic card
  // sees exactly which listings to fix.
  const [photoBucketFilter, setPhotoBucketFilter] = useState(null);
  const listingsRef = React.useRef(null);

  const focusPhotoBucket = (bucket) => {
    setPhotoBucketFilter(bucket);
    setTimeout(() => {
      listingsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  useEffect(() => {
    let cancelled = false;
    api
      .get("/breeder/analytics/summary")
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => {
        if (cancelled) return;
        if (e?.response?.status === 402) {
          setLocked(true);
        } else {
          toast.error(formatApiError(e));
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <AppShell>
        <div className="text-[#6B6B6B]" data-testid="analytics-loading">Loading your numbers…</div>
      </AppShell>
    );
  }

  if (locked) {
    return (
      <AppShell>
        <div className="max-w-2xl mx-auto" data-testid="analytics-locked">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B] mb-2">Analytics</p>
          <h1 className="font-spectral text-4xl tracking-tight mb-3">Breeder analytics</h1>
          <div className="bg-white border border-dashed border-gold rounded-2xl p-10 text-center">
            <Crown size={40} weight="duotone" className="text-gold mx-auto mb-3" />
            <h2 className="font-spectral text-xl mb-2">{isNativeApp() ? "Included with an active breeder plan" : <>Included with Breeder Pro &amp; Kennel Elite</>}</h2>
            <p className="text-[#6B6B6B] text-sm mb-5 max-w-md mx-auto leading-relaxed">
              Track listing views, inquiry conversion, time-to-sale, revenue, and per-listing
              performance — plus friendly coaching nudges that tell you exactly what to do next.
            </p>
            <CompBanner className="mb-4 max-w-md mx-auto text-left" />
            {isNativeApp() ? (
              <IapUpgradeButton
                packageIds={["pro_monthly", "elite_monthly"]}
                label="Upgrade to Breeder Pro"
                context="analytics_locked"
                fallbackTo="/billing"
                fallbackLabel="My account"
                className="max-w-xs mx-auto"
              />
            ) : (
              <Button asChild className="rounded-full bg-ink hover:bg-crimson text-white">
                <Link to="/billing" data-testid="analytics-upgrade-btn">Upgrade to Pro</Link>
              </Button>
            )}
          </div>
        </div>
      </AppShell>
    );
  }

  if (!data) {
    return <AppShell><div className="text-[#6B6B6B]">No data.</div></AppShell>;
  }

  // "Empty dashboard" detection — if there's literally no signal yet,
  // offer a demo-data toggle so the first impression isn't a wall of
  // zeros. We don't auto-fake — the breeder opts in to see what their
  // dashboard will look like once traffic lands.
  const totalSignal =
    (data.funnel?.kennel_views || 0) +
    (data.funnel?.listing_views || 0) +
    (data.funnel?.inquiries || 0) +
    (data.lifetime?.dogs_registered || 0);
  const isEmpty = totalSignal === 0;
  const view = showDemo ? DEMO_DATA : data;

  return (
    <AppShell>
      <div className="space-y-8" data-testid="analytics-page">
        <header className="pb-5 relative after:absolute after:left-0 after:right-0 after:bottom-0 after:h-px after:bg-gold-hairline flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-gold font-bold">
              <ChartBar size={14} weight="duotone" /> Analytics · last {view.period_days} days
            </div>
            <h1 className="font-spectral font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] mt-3">
              How your kennel is doing
            </h1>
            <p className="text-sm text-[#6B6B6B] mt-3 max-w-2xl leading-relaxed">
              A real, friendly read on where your listings are gaining traction — and where a
              small change could turn a few more inquiries into sales.
            </p>
          </div>
          {isEmpty && (
            <button
              onClick={() => setShowDemo((v) => !v)}
              data-testid="analytics-demo-toggle"
              className={`h-9 px-4 rounded-full text-[11px] font-bold uppercase tracking-[0.12em] transition-colors ${
                showDemo
                  ? "bg-gold text-ink"
                  : "bg-white border border-stone-300 text-stone-700 hover:bg-stone-100"
              }`}
            >
              {showDemo ? "Showing demo · tap to hide" : "Show me a preview"}
            </button>
          )}
        </header>

        {showDemo && (
          <div
            className="rounded-2xl bg-gold/10 border border-gold/40 px-4 py-3 text-[12px] text-ink/85 flex items-center gap-2"
            data-testid="analytics-demo-banner"
          >
            <Sparkle size={14} weight="duotone" className="text-gold" />
            <span>
              <strong>Preview mode:</strong> these numbers are a sample of what your
              dashboard looks like once buyers start finding your listings. Your real data
              will replace this as soon as it lands.
            </span>
          </div>
        )}

        <LifetimeStrip lifetime={view.lifetime} watchers={view.watchers} />

        <KPIStrip kpis={view.kpis} primaryKennel={view.primary_kennel} />

        {view.insights?.length > 0 && (
          <section className="space-y-3" data-testid="analytics-insights">
            {view.insights.map((ins, i) => (
              <InsightCard key={i} insight={ins} />
            ))}
          </section>
        )}

        <FunnelChart funnel={view.funnel} primaryKennel={view.primary_kennel} />

        <div className="grid lg:grid-cols-2 gap-4">
          <BestTimeCard best={view.best_time} />
          <PhotoDiagnosticCard diag={view.photo_diagnostic} onFocusBucket={focusPhotoBucket} />
        </div>

        {(view.repeat_buyers?.spotlight || []).length > 0 && (
          <RepeatBuyersCard repeat={view.repeat_buyers} />
        )}

        {(view.tier === "kennel_elite" || view.tier === "admin") && (
          <EliteCohortCard tier={view.tier} />
        )}

        <div ref={listingsRef}>
          <ListingsTable
            rows={view.listings || []}
            photoBucketFilter={photoBucketFilter}
            onClearFilter={() => setPhotoBucketFilter(null)}
          />
        </div>
      </div>
    </AppShell>
  );
}

// ---------------------------------------------------------------------------

function KPIStrip({ kpis, primaryKennel }) {
  return (
    <section
      className="grid grid-cols-2 md:grid-cols-4 gap-3"
      data-testid="analytics-kpi-strip"
    >
      <KPI
        icon={CurrencyDollar}
        label="Revenue"
        value={`$${(kpis.revenue_usd || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
        prev={kpis.revenue_prev_usd}
        current={kpis.revenue_usd}
        href="/payouts"
        testId="kpi-revenue"
      />
      <KPI
        icon={ChatCircle}
        label="Inquiries"
        value={kpis.inquiries}
        prev={kpis.inquiries_prev}
        current={kpis.inquiries}
        href="/inbox"
        testId="kpi-inquiries"
      />
      <KPI
        icon={Storefront}
        label="Active listings"
        value={kpis.active_listings}
        href="/dogs"
        testId="kpi-active-listings"
        flat
      />
      <KPI
        icon={Clock}
        label="Avg. time-to-sale"
        value={kpis.avg_time_to_sale_days != null ? `${kpis.avg_time_to_sale_days} days` : "—"}
        sub={kpis.avg_time_to_sale_days == null ? "Awaiting your first sale" : "Across recent sales"}
        href={kpis.avg_time_to_sale_days != null ? "/escrow/my" : null}
        testId="kpi-time-to-sale"
        flat
      />
    </section>
  );
}

function KPI({ icon: Icon, label, value, prev, current, sub, flat, href, testId }) {
  let delta = null;
  if (!flat && current != null && prev != null) {
    if (prev === 0 && current === 0) {
      delta = { label: "—", positive: null };
    } else if (prev === 0) {
      delta = { label: "new", positive: true };
    } else {
      const pct = Math.round(((current - prev) / prev) * 100);
      delta = { label: `${pct >= 0 ? "+" : ""}${pct}%`, positive: pct >= 0 };
    }
  }
  const inner = (
    <>
      <div className="flex items-center justify-between">
        <Icon size={16} weight="duotone" className="text-gold" />
        {delta && delta.positive != null && (
          <span
            className={`inline-flex items-center gap-1 text-[11px] font-bold ${
              delta.positive ? "text-emerald-700" : "text-crimson"
            }`}
          >
            {delta.positive ? <TrendUp size={11} weight="bold" /> : <TrendDown size={11} weight="bold" />}
            {delta.label}
          </span>
        )}
      </div>
      <p className="text-[10px] uppercase tracking-[0.18em] text-ink/55 font-bold mt-3">{label}</p>
      <p className="font-spectral text-3xl mt-1 font-tabular">{value}</p>
      <p className="text-[11px] text-[#6B6B6B] mt-1 leading-snug flex items-center gap-1">
        {sub || (!flat && prev != null
          ? `vs. prior 30: ${typeof prev === "number" && label === "Revenue"
              ? `$${prev.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
              : prev}`
          : "")}
        {href && <ArrowRight size={10} weight="bold" className="opacity-50" />}
      </p>
    </>
  );
  const baseClass = "bg-white border border-[#EAEAEA] rounded-2xl p-4 transition-colors";
  if (href) {
    return (
      <Link
        to={href}
        className={`${baseClass} hover:border-gold hover:bg-stone-50 block`}
        data-testid={testId}
      >
        {inner}
      </Link>
    );
  }
  return (
    <div className={baseClass} data-testid={testId}>
      {inner}
    </div>
  );
}

// ---------------------------------------------------------------------------

function InsightCard({ insight }) {
  const Icon = INSIGHT_ICONS[insight.icon] || Sparkle;
  const isWin = insight.tone === "win";
  return (
    <div
      className={`rounded-2xl border p-5 flex items-start gap-4 ${
        isWin
          ? "bg-emerald-50/50 border-emerald-200"
          : "bg-[#FFF8E6] border-gold/40"
      }`}
      data-testid={`insight-${insight.icon}`}
    >
      <div
        className={`h-10 w-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
          isWin ? "bg-emerald-100 text-emerald-700" : "bg-white text-gold"
        }`}
      >
        <Icon size={18} weight="duotone" />
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-spectral text-lg leading-tight">{insight.title}</h3>
        <p className="text-sm text-[#3B3B3B] mt-1.5 leading-relaxed">{insight.body}</p>
        {insight.cta_href && (
          <Link
            to={insight.cta_href}
            className="inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-[0.1em] text-ink mt-3 hover:text-crimson"
            data-testid="insight-cta"
          >
            {insight.cta_label || "Open"} <ArrowRight size={12} weight="bold" />
          </Link>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function FunnelChart({ funnel, primaryKennel }) {
  const kennelHref = primaryKennel?.id ? `/breeders/${primaryKennel.id}` : null;
  const stages = [
    { key: "kennel_views", label: "Kennel views", icon: Eye, href: kennelHref, hint: "Open your public kennel page" },
    { key: "listing_views", label: "Listing views", icon: Eye, href: "/dogs", hint: "Audit your listings" },
    { key: "inquiries", label: "Inquiries", icon: ChatCircle, href: "/inbox", hint: "Reply in inbox" },
    { key: "replies", label: "You replied", icon: ChatCircle, href: "/inbox", hint: "Reply in inbox" },
    { key: "sales", label: "Sales", icon: Tag, href: "/escrow/my", hint: "Open sales & escrow" },
  ];
  const max = Math.max(1, ...stages.map((s) => funnel[s.key] || 0));
  return (
    <section
      className="bg-white border border-[#EAEAEA] rounded-2xl p-5"
      data-testid="analytics-funnel"
    >
      <h2 className="font-spectral text-xl mb-1">Where your buyers fall off</h2>
      <p className="text-xs text-[#6B6B6B] mb-5 leading-relaxed">
        Each stage shows how many people made it that far in the last 30 days.
        Tap any stage to jump straight to it.
      </p>
      <ul className="space-y-3">
        {stages.map((s, i) => {
          const count = funnel[s.key] || 0;
          const pct = Math.round((count / max) * 100);
          const prev = i > 0 ? funnel[stages[i - 1].key] : null;
          const conv = prev && prev > 0 ? Math.round((count / prev) * 100) : null;
          const Icon = s.icon;
          const rowContent = (
            <>
              <div className="flex items-center justify-between text-[12.5px]">
                <span className="flex items-center gap-1.5 font-semibold">
                  <Icon size={13} weight="duotone" className="text-gold" />
                  {s.label}
                  {s.href && <ArrowRight size={10} weight="bold" className="opacity-40" />}
                </span>
                <span className="font-tabular text-ink flex items-center gap-2">
                  <strong>{count}</strong>
                  {conv != null && (
                    <span className="text-[10.5px] text-[#6B6B6B] font-normal">
                      ({conv}% of previous)
                    </span>
                  )}
                </span>
              </div>
              <div className="h-2 mt-1 bg-stone-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-ink rounded-full transition-all"
                  style={{ width: `${pct}%` }}
                />
              </div>
            </>
          );
          if (s.href) {
            return (
              <li key={s.key} data-testid={`funnel-${s.key}`}>
                <Link
                  to={s.href}
                  title={s.hint}
                  className="block rounded-lg -mx-2 px-2 py-1 hover:bg-stone-50 transition-colors"
                >
                  {rowContent}
                </Link>
              </li>
            );
          }
          return (
            <li key={s.key} data-testid={`funnel-${s.key}`}>
              {rowContent}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

// ---------------------------------------------------------------------------

function ListingsTable({ rows, photoBucketFilter, onClearFilter }) {
  const [sort, setSort] = useState("views"); // "views" | "inquiries" | "days"
  const [boosting, setBoosting] = useState(null); // dog_id being upgraded

  // Photo-bucket filter (lit from clicking the diagnostic card).
  let displayed = rows;
  if (photoBucketFilter) {
    displayed = rows.filter((r) => {
      const pc = r.photo_count ?? 0;
      if (photoBucketFilter === "0-1") return pc <= 1;
      if (photoBucketFilter === "2-4") return pc >= 2 && pc <= 4;
      return pc >= 5;
    });
  }
  const sorted = [...displayed].sort((a, b) => {
    if (sort === "inquiries") return (b.inquiries_total || 0) - (a.inquiries_total || 0);
    if (sort === "days") return (b.days_listed || 0) - (a.days_listed || 0);
    return (b.views_total || 0) - (a.views_total || 0);
  });

  // Tier-2 inline boost: routes through `/billing/checkout` with the
  // existing `marketplace_feature` SKU. Admins get a free promote;
  // everyone else is redirected to Stripe.
  const handleBoost = async (dog) => {
    if (boosting) return;
    setBoosting(dog.id);
    try {
      const { data } = await api.post("/billing/checkout", {
        package_id: "marketplace_feature",
        origin_url: webOrigin(),
        dog_id: dog.id,
      });
      if (data.free) {
        toast.success(`${dog.name} is now featured at the top of the marketplace for 30 days.`);
      } else if (data.url) {
        window.location.href = data.url;
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBoosting(null);
    }
  };

  if (rows.length === 0) {
    return (
      <section
        className="bg-stone-50 border border-stone-200 rounded-2xl p-8 text-center"
        data-testid="analytics-listings-empty"
      >
        <p className="text-sm text-[#6B6B6B]">
          No listings yet. <Link to="/dogs/new" className="text-ink underline hover:text-crimson">Add your first dog</Link> to start tracking performance.
        </p>
      </section>
    );
  }

  return (
    <section
      className="bg-white border border-[#EAEAEA] rounded-2xl overflow-hidden"
      data-testid="analytics-listings"
    >
      <div className="flex items-center justify-between p-5 border-b border-[#F0F0F0] flex-wrap gap-2">
        <div>
          <h2 className="font-spectral text-xl">Per-listing performance</h2>
          <p className="text-xs text-[#6B6B6B] mt-0.5">
            Click any row to open it. Use the buttons in the right column for quick wins.
          </p>
        </div>
        <div className="flex items-center gap-1">
          {[
            ["views", "Views"],
            ["inquiries", "Inquiries"],
            ["days", "Days listed"],
          ].map(([k, l]) => (
            <button
              key={k}
              onClick={() => setSort(k)}
              data-testid={`sort-${k}`}
              className={`h-7 px-2.5 rounded-full text-[10.5px] font-bold uppercase tracking-[0.1em] ${
                sort === k
                  ? "bg-ink text-white"
                  : "bg-white border border-stone-300 text-stone-700 hover:bg-stone-100"
              }`}
            >
              {l}
            </button>
          ))}
        </div>
      </div>

      {photoBucketFilter && (
        <div
          className="flex items-center justify-between bg-gold/10 border-b border-gold/30 px-5 py-2.5 text-[12px]"
          data-testid="listings-bucket-filter-banner"
        >
          <span className="text-ink">
            Showing only listings with <strong>{photoBucketFilter}</strong> photos —
            adding more usually pulls 2–4× the inquiries.
          </span>
          <button
            onClick={onClearFilter}
            className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink/70 hover:text-crimson"
          >
            Clear filter ×
          </button>
        </div>
      )}

      <div className="overflow-x-auto -mx-3 sm:mx-0">
        <table className="w-full text-[12.5px] min-w-[640px]">
          <thead className="bg-stone-50 text-[10px] uppercase tracking-[0.1em] text-ink/55">
            <tr>
              <th className="text-left p-3">Dog</th>
              <th className="text-left p-3">Status</th>
              <th className="text-right p-3">Views</th>
              <th className="text-right p-3">Inquiries</th>
              <th className="text-right p-3">Days listed</th>
              <th className="text-right p-3">Asking</th>
              <th className="text-right p-3">Quick win</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#F0F0F0]">
            {sorted.map((r) => (
              <ListingRow
                key={r.id}
                row={r}
                boosting={boosting === r.id}
                onBoost={() => handleBoost(r)}
              />
            ))}
            {sorted.length === 0 && (
              <tr>
                <td colSpan={7} className="p-6 text-center text-[#6B6B6B] text-[12px]">
                  No listings match this filter.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/**
 * Per-listing row with a contextual "Quick win" call-to-action.
 *
 * The right column surfaces the single most useful action for the row
 * based on its state — 0 photos prompts "Add photos", a hot listing
 * with 5+ inquiries but no sale prompts "Boost $29", a stale listing
 * prompts "Refresh", and a healthy listing falls back to "View public".
 */
function ListingRow({ row, boosting, onBoost }) {
  const pc = row.photo_count ?? 0;
  const vc = row.video_count ?? 0;
  const isHot = row.for_sale && (row.inquiries_total || 0) >= 5;
  const isStale = row.for_sale && (row.days_listed || 0) >= 60;
  const isThin = row.for_sale && pc <= 1;
  // After the seller has at least 2 photos but zero working videos,
  // the next-highest-leverage move is to upload a clip. Buyers cite
  // "seeing the dog move" as the single most decisive factor on a
  // litter. We surface this BEFORE the generic "view public" so it
  // gets prime real-estate while there's still time to convert.
  const needsVideo = row.for_sale && pc >= 2 && vc === 0;

  let cta = null;
  if (isThin) {
    cta = (
      <Link
        to={`/dogs/${row.id}/edit?focus=photos`}
        className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full bg-amber-100 text-amber-900 text-[10.5px] font-bold uppercase tracking-[0.08em] hover:bg-amber-200"
        data-testid={`quickwin-add-photos-${row.id}`}
      >
        <CameraPlus size={11} weight="bold" /> Add photos
      </Link>
    );
  } else if (needsVideo) {
    cta = (
      <Link
        to={`/dogs/${row.id}/edit?focus=videos`}
        className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full bg-crimson/10 text-crimson text-[10.5px] font-bold uppercase tracking-[0.08em] hover:bg-crimson/20"
        data-testid={`quickwin-add-video-${row.id}`}
        title="Buyers love working-dog clips — show off her drive"
      >
        <VideoCamera size={11} weight="bold" /> Add video
      </Link>
    );
  } else if (isHot && !isIOS()) {
    cta = (
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); onBoost(); }}
        disabled={boosting}
        className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full bg-gold text-ink text-[10.5px] font-bold uppercase tracking-[0.08em] hover:bg-gold/85 disabled:opacity-60"
        data-testid={`quickwin-boost-${row.id}`}
      >
        <Lightning size={11} weight="bold" /> {boosting ? "Opening…" : "Boost $29"}
      </button>
    );
  } else if (isStale) {
    cta = (
      <Link
        to={`/dogs/${row.id}?edit=price`}
        className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full bg-stone-200 text-stone-800 text-[10.5px] font-bold uppercase tracking-[0.08em] hover:bg-stone-300"
        data-testid={`quickwin-refresh-${row.id}`}
      >
        Refresh price
      </Link>
    );
  } else if (row.for_sale) {
    cta = (
      <Link
        to={`/dogs/${row.id}`}
        className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full bg-white border border-stone-300 text-stone-700 text-[10.5px] font-bold uppercase tracking-[0.08em] hover:bg-stone-100"
        data-testid={`quickwin-view-${row.id}`}
      >
        View public
      </Link>
    );
  } else {
    // Off-market dog — just open the detail page; the existing "List
    // for sale" CTA there routes through the proper paid checkout flow.
    cta = (
      <Link
        to={`/dogs/${row.id}`}
        className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full bg-white border border-stone-300 text-stone-700 text-[10.5px] font-bold uppercase tracking-[0.08em] hover:bg-stone-100"
        data-testid={`quickwin-list-${row.id}`}
      >
        List for sale
      </Link>
    );
  }

  return (
    <tr data-testid={`listing-row-${row.id}`} className="hover:bg-stone-50">
      <td className="p-3">
        <Link to={`/dogs/${row.id}`} className="flex items-center gap-2.5 group">
          {row.photo ? (
            <img src={row.photo} alt={row.name} className="h-9 w-9 rounded-lg object-cover" />
          ) : (
            <div className="h-9 w-9 rounded-lg bg-stone-200" />
          )}
          <div className="min-w-0">
            <div className="font-semibold text-ink group-hover:text-crimson truncate">
              {row.name || "(untitled)"}
            </div>
            <div className="text-[10.5px] text-[#6B6B6B] truncate">
              {row.breed || ""}{pc >= 0 ? ` · ${pc} photo${pc === 1 ? "" : "s"}` : ""}
            </div>
          </div>
        </Link>
      </td>
      <td className="p-3">
        {row.for_sale ? (
          <span className="inline-flex items-center px-1.5 h-5 text-[10px] font-bold rounded bg-emerald-100 text-emerald-700 uppercase tracking-[0.1em]">
            For sale
          </span>
        ) : (
          <span className="text-[10.5px] text-[#6B6B6B]">Off-market</span>
        )}
      </td>
      <td className="p-3 text-right font-tabular">{row.views_total || 0}</td>
      <td className="p-3 text-right font-tabular font-semibold">{row.inquiries_total || 0}</td>
      <td className="p-3 text-right font-tabular text-[#6B6B6B]">
        {row.days_listed != null ? row.days_listed : "—"}
      </td>
      <td className="p-3 text-right font-tabular text-[#6B6B6B]">
        {row.asking_price ? `$${Number(row.asking_price).toLocaleString()}` : "—"}
      </td>
      <td className="p-3 text-right">{cta}</td>
    </tr>
  );
}


// ---------------------------------------------------------------------------
// Lifetime stats strip — always populated, anti-empty-dashboard guard
// ---------------------------------------------------------------------------
function LifetimeStrip({ lifetime, watchers }) {
  if (!lifetime) return null;
  const items = [
    { label: "Dogs registered", value: lifetime.dogs_registered || 0, href: "/dogs" },
    { label: "Litters", value: lifetime.litters_registered || 0, href: "/litters" },
    { label: "All-time inquiries", value: lifetime.inquiries || 0, href: "/inbox" },
    { label: "All-time sales", value: lifetime.sales || 0, href: "/escrow/my" },
    {
      label: "All-time revenue",
      value: `$${(lifetime.revenue_usd || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
      href: "/payouts",
    },
    {
      label: "Buyers watching",
      value: watchers?.total || 0,
      accent: (watchers?.total || 0) > 0,
      href: (watchers?.total || 0) > 0 ? "/feed" : null,
      hint: "Post a kennel update — watchers see it first",
    },
  ];
  return (
    <section
      className="bg-ink text-white rounded-2xl px-5 py-4 flex items-center justify-between flex-wrap gap-y-3"
      data-testid="analytics-lifetime-strip"
    >
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-gold font-bold">
        <Sparkle size={12} weight="duotone" /> Lifetime
      </div>
      <div className="grid grid-cols-3 md:grid-cols-6 gap-x-6 gap-y-3 flex-1 ml-6">
        {items.map((it) => {
          const inner = (
            <>
              <p className="text-[9.5px] uppercase tracking-[0.14em] text-white/55 font-bold flex items-center gap-1">
                {it.label}
                {it.href && <ArrowRight size={9} weight="bold" className="opacity-50" />}
              </p>
              <p
                className={`text-lg font-spectral mt-0.5 font-tabular ${
                  it.accent ? "text-gold" : "text-white"
                }`}
              >
                {it.value}
              </p>
            </>
          );
          if (it.href) {
            return (
              <Link
                key={it.label}
                to={it.href}
                title={it.hint || ""}
                className="block hover:bg-white/5 rounded-md -m-1 p-1 transition-colors"
                data-testid={`lifetime-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {inner}
              </Link>
            );
          }
          return <div key={it.label}>{inner}</div>;
        })}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Best-time-to-list — hour-bucket bar chart
// ---------------------------------------------------------------------------
function BestTimeCard({ best }) {
  if (!best) return null;
  const total = best.total_inquiries_analyzed || 0;
  const hours = best.hour_buckets || [];
  const max = Math.max(1, ...hours);

  return (
    <section
      className="bg-white border border-[#EAEAEA] rounded-2xl p-5"
      data-testid="analytics-best-time"
    >
      <div className="flex items-center gap-2 mb-1">
        <CalendarBlank size={16} weight="duotone" className="text-gold" />
        <h2 className="font-spectral text-xl">When buyers come hunting</h2>
      </div>
      {total < 10 ? (
        <p className="text-sm text-[#6B6B6B] mt-3 leading-relaxed">
          We'll find your best-performing window once you've received at least 10 inquiries.
          You're at <strong>{total}</strong> so far — keep going.
        </p>
      ) : (
        <>
          <p className="text-sm text-[#3B3B3B] mt-1 leading-relaxed">
            Most of your inquiries land on{" "}
            <strong>{best.day}</strong>{" "}
            {best.hour_window && (
              <>between <strong>{best.hour_window}</strong></>
            )}
            . Time your next listing or price refresh for that window.
          </p>
          <div className="mt-4 flex items-end gap-px h-16" data-testid="best-time-hour-chart">
            {hours.map((c, h) => {
              const pct = Math.round((c / max) * 100);
              return (
                <div
                  key={h}
                  className="flex-1 bg-gold/80 rounded-t-sm transition-all"
                  style={{ height: `${Math.max(6, pct)}%` }}
                  title={`${h}:00 — ${c} inquiries`}
                />
              );
            })}
          </div>
          <div className="flex justify-between text-[10px] text-[#6B6B6B] mt-1.5">
            <span>12am</span><span>6am</span><span>noon</span><span>6pm</span><span>11pm</span>
          </div>
        </>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Photo diagnostic — avg inquiries by photo-count bucket
// ---------------------------------------------------------------------------
function PhotoDiagnosticCard({ diag, onFocusBucket }) {
  if (!diag) return null;
  const buckets = [
    { key: "0-1", label: "0–1 photos", data: diag["0-1"] || { listings: 0, avg_inquiries: 0 } },
    { key: "2-4", label: "2–4 photos", data: diag["2-4"] || { listings: 0, avg_inquiries: 0 } },
    { key: "5+", label: "5+ photos", data: diag["5+"] || { listings: 0, avg_inquiries: 0 } },
  ];
  const max = Math.max(1, ...buckets.map((b) => b.data.avg_inquiries));
  const hasAnyListings = buckets.some((b) => b.data.listings > 0);

  return (
    <section
      className="bg-white border border-[#EAEAEA] rounded-2xl p-5"
      data-testid="analytics-photo-diagnostic"
    >
      <div className="flex items-center gap-2 mb-1">
        <CameraPlus size={16} weight="duotone" className="text-gold" />
        <h2 className="font-spectral text-xl">Photos drive interest</h2>
      </div>
      <p className="text-sm text-[#3B3B3B] mt-1 leading-relaxed">
        Avg. inquiries per listing, grouped by how many photos you uploaded.
        Tap a bucket to see those listings.
      </p>
      {!hasAnyListings ? (
        <p className="text-xs text-[#6B6B6B] mt-4">
          Add a few dogs to see how photo count affects your inquiry volume.
        </p>
      ) : (
        <ul className="space-y-2.5 mt-4">
          {buckets.map((b) => {
            const pct = Math.round((b.data.avg_inquiries / max) * 100);
            const isWinner = b.key === "5+" && b.data.avg_inquiries === max && b.data.listings > 0;
            const clickable = b.data.listings > 0;
            const inner = (
              <>
                <div className="flex items-center justify-between text-[12px]">
                  <span className={isWinner ? "font-bold text-ink" : "text-[#3B3B3B]"}>
                    {b.label}{" "}
                    <span className="text-[10.5px] text-[#6B6B6B] font-normal">
                      ({b.data.listings} listing{b.data.listings === 1 ? "" : "s"})
                    </span>
                  </span>
                  <span className="font-tabular">
                    <strong>{b.data.avg_inquiries}</strong>{" "}
                    <span className="text-[10.5px] text-[#6B6B6B] font-normal">avg inquiries</span>
                  </span>
                </div>
                <div className="h-2 bg-stone-100 rounded-full mt-1 overflow-hidden">
                  <div
                    className={`h-full rounded-full ${isWinner ? "bg-gold" : "bg-ink/80"}`}
                    style={{ width: `${Math.max(4, pct)}%` }}
                  />
                </div>
              </>
            );
            if (clickable) {
              return (
                <li key={b.key} data-testid={`photo-bucket-${b.key}`}>
                  <button
                    type="button"
                    onClick={() => onFocusBucket?.(b.key)}
                    className="block w-full text-left rounded-lg hover:bg-stone-50 -mx-2 px-2 py-1 transition-colors"
                  >
                    {inner}
                  </button>
                </li>
              );
            }
            return (
              <li key={b.key} data-testid={`photo-bucket-${b.key}`}>
                {inner}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Repeat-buyer spotlight
// ---------------------------------------------------------------------------
function RepeatBuyersCard({ repeat }) {
  return (
    <section
      className="bg-emerald-50/60 border border-emerald-200 rounded-2xl p-5"
      data-testid="analytics-repeat-buyers"
    >
      <div className="flex items-center gap-2 mb-1">
        <Heart size={16} weight="duotone" className="text-emerald-700" />
        <h2 className="font-spectral text-xl">Warm leads worth a reply</h2>
      </div>
      <p className="text-sm text-[#3B3B3B] mt-1 leading-relaxed">
        These buyers have inquired with you more than once, or already bought from you and came
        back. Returning buyers close 5× faster than cold leads.
      </p>
      <ul className="mt-4 space-y-2">
        {repeat.spotlight.map((b) => (
          <li
            key={b.id}
            className="flex items-center justify-between bg-white rounded-xl px-3 py-2"
            data-testid={`repeat-buyer-${b.id}`}
          >
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="h-8 w-8 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700">
                <UserCircle size={18} weight="duotone" />
              </div>
              <div className="min-w-0">
                <div className="font-semibold text-[13px] truncate">{b.name}</div>
                <div className="text-[10.5px] text-[#6B6B6B] truncate">{b.email}</div>
              </div>
            </div>
            <div className="text-right">
              {b.is_past_buyer && (
                <span className="inline-flex items-center px-1.5 h-4 text-[9.5px] font-bold rounded bg-emerald-700 text-white uppercase tracking-[0.1em]">
                  Past buyer
                </span>
              )}
              <div className="text-[11px] text-[#6B6B6B] mt-0.5">
                {b.inquiries} {b.inquiries === 1 ? "inquiry" : "inquiries"}
              </div>
            </div>
          </li>
        ))}
      </ul>
      <Link
        to="/inbox"
        className="inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-[0.1em] text-emerald-800 mt-4 hover:text-emerald-900"
        data-testid="repeat-buyers-cta"
      >
        Reply in inbox <ArrowRight size={12} weight="bold" />
      </Link>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Sample/demo payload — friendly preview when the dashboard is empty
// ---------------------------------------------------------------------------
const DEMO_DATA = {
  period_days: 30,
  primary_kennel: { id: "demo-kennel", slug: "your-kennel" },
  kpis: {
    revenue_usd: 4250.0,
    revenue_prev_usd: 2800.0,
    inquiries: 32,
    inquiries_prev: 19,
    active_listings: 6,
    avg_time_to_sale_days: 21.5,
  },
  lifetime: {
    dogs_registered: 18,
    litters_registered: 4,
    inquiries: 142,
    sales: 9,
    revenue_usd: 22400,
  },
  watchers: { kennel_watchers: 14, litter_watchers: 7, total: 21 },
  best_time: {
    day: "Sun",
    hour_window: "6pm–9pm",
    day_buckets: [3, 4, 5, 2, 6, 8, 12],
    hour_buckets: [0, 0, 0, 0, 0, 0, 1, 1, 2, 1, 2, 2, 3, 2, 3, 4, 5, 7, 9, 11, 6, 2, 1, 0],
    total_inquiries_analyzed: 75,
  },
  photo_diagnostic: {
    "0-1": { listings: 2, avg_inquiries: 1.5 },
    "2-4": { listings: 3, avg_inquiries: 4.7 },
    "5+": { listings: 1, avg_inquiries: 8.0 },
  },
  repeat_buyers: {
    repeat_inquirers: 3,
    returning_buyers: 1,
    spotlight: [
      { id: "demo-1", name: "Sarah Mitchell", email: "sarah.m@example.com", is_past_buyer: true, inquiries: 2 },
      { id: "demo-2", name: "James Lee", email: "jlee@example.com", is_past_buyer: false, inquiries: 3 },
      { id: "demo-3", name: "Anita Patel", email: "anita.p@example.com", is_past_buyer: false, inquiries: 2 },
    ],
  },
  funnel: {
    kennel_views: 1240,
    listing_views: 327,
    inquiries: 32,
    replies: 27,
    sales: 4,
  },
  listings: [
    { id: "demo-a", name: "Bella", breed: "Cavalier King Charles", photo: null, photo_count: 6, for_sale: true, asking_price: 3200, days_listed: 18, views_total: 142, inquiries_total: 11 },
    { id: "demo-b", name: "Cooper", breed: "Frenchie", photo: null, photo_count: 3, for_sale: true, asking_price: 4500, days_listed: 8, views_total: 96, inquiries_total: 7 },
    { id: "demo-c", name: "Daisy", breed: "Cavalier King Charles", photo: null, photo_count: 0, for_sale: true, asking_price: null, days_listed: 67, views_total: 54, inquiries_total: 4 },
  ],
  insights: [
    {
      tone: "win",
      icon: "trendup",
      title: "You're having a great month",
      body: "Revenue is up 52% versus the prior 30 days. Keep your listings fresh and reply to inquiries within 2 hours to keep the streak going.",
      cta_label: null, cta_href: null,
    },
    {
      tone: "coaching",
      icon: "lightning",
      title: "Bella is getting attention but no offers",
      body: "11 buyers asked about Bella but the listing hasn't sold yet. A small price refresh, a $35 live video tour, or a featured-listing boost usually tips a hot listing over the line.",
      cta_label: "Open Bella", cta_href: "#",
    },
    {
      tone: "coaching",
      icon: "sparkle",
      title: "Your buyers come hunting on Sunday evenings",
      body: "Most of your inquiries land Sunday 6pm–9pm. Time your next litter announcement or price refresh for that window.",
      cta_label: null, cta_href: null,
    },
  ],
};


// ---------------------------------------------------------------------------
// Elite-only cohort analytics card
// ---------------------------------------------------------------------------
// Groups the breeder's dogs by their birth month and surfaces
// per-cohort sell-through rate + average time-to-sale. Strictly
// gated to Kennel Elite (or admin); the backend returns 402 for
// every other tier so the card silently hides itself.

function EliteCohortCard({ tier }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/breeder/analytics/cohort")
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => {
        if (cancelled) return;
        if (e?.response?.status === 402) {
          // shouldn't happen since we already gate on tier in the parent
          setError("Elite tier required.");
        } else {
          setError(formatApiError(e));
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <section
        className="bg-[#0E0E0E] text-white rounded-2xl p-6"
        data-testid="elite-cohort-loading"
      >
        <p className="text-white/70 text-sm">Loading cohort analytics…</p>
      </section>
    );
  }
  if (error || !data) return null;

  const rows = data.cohorts || [];
  const summary = data.summary || {};
  if (!rows.length) {
    return (
      <section
        className="bg-[#0E0E0E] text-white rounded-2xl p-6"
        data-testid="elite-cohort-empty"
      >
        <header className="flex items-center justify-between gap-3 mb-3 flex-wrap">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-gold font-bold">
            <Crown size={14} weight="duotone" /> Cohort analytics · Elite
          </div>
        </header>
        <p className="text-white/70 text-sm">
          Once you have at least one dog registered with a birthdate, your cohort sell-through
          and time-to-sale will appear here — broken down by birth month, so you can compare
          litters across years at a glance.
        </p>
      </section>
    );
  }

  // Color scale for sell-through % (the heatmap intensity).
  const intensity = (pct) => {
    if (pct >= 80) return "bg-gold/90 text-ink";
    if (pct >= 60) return "bg-gold/60 text-ink";
    if (pct >= 40) return "bg-gold/35 text-white";
    if (pct >= 20) return "bg-gold/20 text-white";
    return "bg-white/5 text-white/70";
  };

  return (
    <section
      className="bg-[#0E0E0E] text-white rounded-2xl p-6 relative overflow-hidden"
      data-testid="elite-cohort-card"
    >
      <div
        aria-hidden
        className="absolute -right-16 -top-16 w-56 h-56 rounded-full bg-gold/15 blur-3xl"
      />
      <div className="relative">
        <header className="flex items-baseline justify-between gap-3 flex-wrap mb-4">
          <div>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-gold font-bold">
              <Crown size={14} weight="duotone" /> Cohort analytics · Elite
            </div>
            <h2 className="font-spectral text-2xl tracking-tight mt-1.5">
              Sell-through by birth cohort
            </h2>
            <p className="text-sm text-white/70 mt-1 max-w-2xl leading-relaxed">
              Each row is a birth month. The brighter the gold, the higher the cohort's
              sell-through rate. Use this to compare litters year over year — and to spot
              breeding pairs whose offspring move fastest.
            </p>
          </div>
          <div className="text-right text-xs text-white/70">
            <div>
              <span className="font-spectral text-2xl text-gold-light">
                {summary.avg_sell_through_pct}%
              </span>
            </div>
            <div>avg sell-through · {summary.cohorts_shown} cohorts</div>
            {summary.avg_time_to_sale_days != null && (
              <div className="mt-0.5">avg time-to-sale: {summary.avg_time_to_sale_days}d</div>
            )}
          </div>
        </header>

        <div className="overflow-x-auto" data-testid="elite-cohort-table">
          <table className="w-full text-xs">
            <thead className="text-[10px] uppercase tracking-wider text-white/50">
              <tr>
                <th className="text-left pb-2 pr-3">Cohort</th>
                <th className="text-right pb-2 pr-3">Born</th>
                <th className="text-right pb-2 pr-3">Sold</th>
                <th className="text-right pb-2 pr-3">Available</th>
                <th className="text-right pb-2 pr-3">DNA✓</th>
                <th className="text-right pb-2 pr-3">Sell-through</th>
                <th className="text-right pb-2">Time-to-sale</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.cohort}
                  className="border-t border-white/10"
                  data-testid={`elite-cohort-row-${r.cohort}`}
                >
                  <td className="py-2 pr-3">
                    <div className="font-spectral text-base">{r.cohort}</div>
                    {r.dominant_breed && (
                      <div className="text-[10px] text-white/50 uppercase tracking-wider">
                        {r.dominant_breed}
                      </div>
                    )}
                  </td>
                  <td className="py-2 pr-3 text-right font-tabular">{r.total}</td>
                  <td className="py-2 pr-3 text-right font-tabular">{r.sold}</td>
                  <td className="py-2 pr-3 text-right font-tabular">{r.available}</td>
                  <td className="py-2 pr-3 text-right font-tabular text-gold">
                    {r.dna_verified || 0}
                  </td>
                  <td className="py-2 pr-3 text-right">
                    <span
                      className={`inline-block min-w-[3.5rem] text-right font-bold rounded px-2 py-0.5 font-tabular ${intensity(
                        r.sell_through_pct,
                      )}`}
                    >
                      {r.sell_through_pct}%
                    </span>
                  </td>
                  <td className="py-2 text-right text-white/80 font-tabular">
                    {r.avg_time_to_sale_days != null ? `${r.avg_time_to_sale_days}d` : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
